import React from "react";
import { GitCompareArrows, ScanEye, Database, ClipboardEdit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";

const n = (v) => Number(v) || 0;

const totalsOf = (floors) => (floors || []).reduce((t, f) => ({
  count: t.count + 1,
  plot: t.plot + n(f.plot_area_sqft),
  open: t.open + n(f.open_area_sqft),
  constructed: t.constructed + n(f.constructed_area_sqft ?? f.area_sqft ?? f.super_builtup_area_sqft),
  carpet: t.carpet + n(f.carpet_area_sqft),
}), { count: 0, plot: 0, open: 0, constructed: 0, carpet: 0 });

const Column = ({ icon: Icon, title, tone, floors, totals, empty, highlight }) => (
  <div className={`border rounded-lg p-3 space-y-2 ${tone}`} data-testid={`compare-col-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <p className="text-xs font-semibold flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {title}</p>
    {(!floors || floors.length === 0) ? (
      <p className="text-xs text-muted-foreground py-3 text-center">{empty}</p>
    ) : (
      <>
        <div className="grid grid-cols-2 gap-1.5 text-center">
          {[["Floors", totals.count], ["Constructed", totals.constructed], ["Carpet", totals.carpet], ["Plot", totals.plot]].map(([lab, val]) => (
            <div key={lab} className={`rounded-md py-1.5 px-1 ${highlight && highlight[lab.toLowerCase()] ? "bg-amber-100 ring-1 ring-amber-300" : "bg-background/70"}`}>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{lab}</p>
              <p className="text-xs font-bold tabular-nums">{Number(val).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>
        <div className="space-y-1">
          {floors.map((f, i) => (
            <div key={i} className="text-[11px] flex items-center justify-between gap-1 border-t pt-1">
              <span className="truncate">
                <span className="font-medium">{f.floor_label || `Floor ${i + 1}`}</span>
                <span className="text-muted-foreground"> • {f.usage_type_name || f.usage_type || "—"}</span>
                {f.property_type_name ? <span className="text-muted-foreground"> • {f.property_type_name}</span> : null}
              </span>
              <span className="tabular-nums shrink-0">{Number(n(f.constructed_area_sqft ?? f.area_sqft ?? f.super_builtup_area_sqft)).toLocaleString("en-IN")} sqft</span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

/**
 * 3-way comparison: AI Captured vs Data Entry vs Collector Reassessment.
 * ai: ai_analysis object ({floors_count, approx_plot_area_sqft, approx_super_builtup_area_sqft, carpet_area_sqft, floor_usage_suggestion})
 * original: assessment_floors saved on the property (data entry)
 * current: collector's floor rows (calculator format: area_sqft = constructed) or stored new_floors
 * nameResolvers (optional): { usage: id->name, ptype: id->name } to label current floors
 */
export const AssessmentComparison = ({ ai, original, current, nameResolvers }) => {
  const aiFloors = (ai?.floor_usage_suggestion || []).map((f) => ({
    floor_label: f.floor_label, usage_type: f.usage_type,
    constructed_area_sqft: f.super_builtup_area_sqft,
    carpet_area_sqft: Math.round(n(f.super_builtup_area_sqft) * 0.8),
  }));
  const aiTotals = ai ? {
    count: ai.floors_count || aiFloors.length,
    plot: n(ai.approx_plot_area_sqft),
    open: 0,
    constructed: n(ai.approx_super_builtup_area_sqft) || totalsOf(aiFloors).constructed,
    carpet: n(ai.carpet_area_sqft) || Math.round(n(ai.approx_super_builtup_area_sqft) * 0.8),
  } : totalsOf(aiFloors);

  const cur = (current || []).map((f) => ({
    ...f,
    usage_type_name: f.usage_type_name || (nameResolvers?.usage ? nameResolvers.usage(f.usage_type_id) : null),
    property_type_name: f.property_type_name || (nameResolvers?.ptype ? nameResolvers.ptype(f.property_type_id) : null),
  }));
  const oTotals = totalsOf(original);
  const cTotals = totalsOf(cur);
  const changed = (original || []).length > 0 && (cur.length > 0) && (
    oTotals.count !== cTotals.count || oTotals.constructed !== cTotals.constructed ||
    oTotals.carpet !== cTotals.carpet || oTotals.plot !== cTotals.plot);
  const highlight = changed ? {
    floors: oTotals.count !== cTotals.count,
    constructed: oTotals.constructed !== cTotals.constructed,
    carpet: oTotals.carpet !== cTotals.carpet,
    plot: oTotals.plot !== cTotals.plot,
  } : null;

  return (
    <Card className="shadow-sm" data-testid="assessment-comparison-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2 flex-wrap">
          <GitCompareArrows className="h-4 w-4" /> Comparative view
          {(original || []).length > 0 && cur.length > 0 && (
            changed
              ? <Badge className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100" data-testid="comparison-changed-badge">Values changed</Badge>
              : <Badge variant="secondary" data-testid="comparison-nochange-badge">No change</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          <Column icon={ScanEye} title="AI Captured" tone="bg-violet-50/60 border-violet-200"
            floors={aiFloors} totals={aiTotals} empty="No AI scan for this property" />
          <Column icon={Database} title="Data Entry" tone="bg-slate-50 border-slate-200"
            floors={original} totals={oTotals} empty="No floor-wise data on record" />
          <Column icon={ClipboardEdit} title="Reassessment" tone="bg-emerald-50/50 border-emerald-200"
            floors={cur} totals={cTotals} empty="Collector has not entered values yet" highlight={highlight} />
        </div>
      </CardContent>
    </Card>
  );
};
